import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import {
  formatOooPeriod,
  getMyOooSettings,
  previewMyOooSettings,
  syncMyOooSettings,
  updateMyOooSettings,
  type EmployeeOooMeSettings,
  type EmployeeOooPreview,
} from "@/lib/employeeOoo";

export function OooSubstitutePanel() {
  const [settings, setSettings] = useState<EmployeeOooMeSettings | null>(null);
  const [preview, setPreview] = useState<EmployeeOooPreview | null>(null);
  const [substitute, setSubstitute] = useState<AutocompleteOption<EmployeeOption> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, previewData] = await Promise.all([
        getMyOooSettings(),
        previewMyOooSettings(),
      ]);
      setSettings(me);
      setPreview(previewData);
      setSubstitute(
        employeeLoginToOption(me.substitute_login ?? "", me.substitute_display) ?? null,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canEdit = settings?.can_edit_substitute ?? false;

  const previewHint = useMemo(() => {
    if (!preview?.automation_enabled) return null;
    if (preview.all_staff_absent_today) {
      return "Heute ist die gesamte Belegschaft abwesend – in der Notiz wird keine Vertretung angezeigt.";
    }
    if (!preview.substitute && settings?.substitute_login) {
      return "Ihre Vertretung (und deren Vertretungskette) ist heute abwesend – in der Notiz wird kein Ansprechpartner genannt.";
    }
    if (!preview.substitute) {
      return "Ohne hinterlegte Vertretung wird in der Abwesenheitsnotiz kein Ansprechpartner genannt.";
    }
    return `Aktuelle Vertretung in der Notiz: ${preview.substitute.display}`;
  }, [preview, settings?.substitute_login]);

  const syncHint = useMemo(() => {
    if (!settings) return null;
    if (settings.last_sync_status === "error" && settings.last_sync_error) {
      return `Outlook-Sync fehlgeschlagen: ${settings.last_sync_error}`;
    }
    if (settings.last_sync_at) {
      return `Zuletzt nach Outlook übertragen: ${new Date(settings.last_sync_at).toLocaleString("de-DE")}`;
    }
    return "Die Notiz wird nach dem Speichern automatisch nach Outlook übertragen.";
  }, [settings]);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const saved = await updateMyOooSettings({
        substitute_login: substitute?.value.login ?? null,
        substitute_display: substitute?.value.display ?? null,
      });
      setSettings(saved);
      const previewData = await previewMyOooSettings();
      setPreview(previewData);
      if (saved.last_sync_status === "error") {
        toast.error(saved.last_sync_error || "Outlook-Sync fehlgeschlagen.");
      } else {
        toast.success("Vertretung gespeichert und nach Outlook übertragen.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncMyOooSettings();
      await load();
      toast.success("Abwesenheitsnotiz nach Outlook übertragen.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Outlook-Sync fehlgeschlagen.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-6 flex justify-center rounded-ios border border-separator bg-card py-10 shadow-ios">
        <Spinner />
      </div>
    );
  }

  if (!settings?.automation_enabled) {
    return null;
  }

  return (
    <div className="mb-6 rounded-ios border border-separator bg-card p-5 shadow-ios">
      <h3 className="text-lg font-semibold text-label">Abwesenheitsnotiz</h3>
      <p className="mt-2 text-sm text-secondary">
        Für Sie ist die automatische Outlook-Abwesenheitsnotiz aktiv. Wählen Sie hier Ihre
        Vertretung für dringende Fälle. Ist diese ebenfalls abwesend, wird deren Vertretung
        verwendet.
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <EmployeeLockedAutocomplete
          label="Meine Vertretung"
          placeholder="Mitarbeiter suchen …"
          value={substitute}
          onChange={setSubstitute}
        />
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-secondary">Aktueller Zeitraum: </span>
            <span className="font-medium text-label">{formatOooPeriod(preview?.active_period ?? null)}</span>
          </div>
          {previewHint && (
            <Alert variant="info" title="Hinweis">
              {previewHint}
            </Alert>
          )}
          {syncHint && (
            <p className="text-xs text-secondary">{syncHint}</p>
          )}
        </div>
      </div>

      {preview?.internal_message && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-label">
            Vorschau der Abwesenheitsnotiz
          </summary>
          <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-xs text-secondary">
            {preview.internal_message}
          </pre>
        </details>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={() => void handleSync()} disabled={syncing || saving}>
          {syncing ? "Übertrage …" : "Nach Outlook übertragen"}
        </Button>
        <Button onClick={() => void handleSave()} disabled={saving || syncing}>
          {saving ? "Speichere …" : "Vertretung speichern"}
        </Button>
      </div>
    </div>
  );
}
